/* eslint-disable quotes */
import { api } from './index';
import { getApplication, onSubmitAgent, putApplication } from './applicationAPI';
import { PRODUCTION } from '../components/PortalApp';
import { navigateWithData } from '../navigation/navigationHistory';
import { traverseObject } from '../utils/objectUtils';
import { GA_CONFIGURATION, sendGoogleAnalyticsEvent } from "../utils/googleAnalyticsUtils";

const handleError = async (error, systemId, applicationNumber, where) => {
  if (!PRODUCTION) console.log('Failure in ' + where + '(): ' + error.message);
  if ((error.response && (error.response.status === 503)) || (error.message == 'Network Error')) {
    navigateWithData('/server-maintenance', applicationNumber);
  } else {
    await onSubmitAgent(systemId, applicationNumber);
  }
}

export const updatePaymentPlansAndBuy = async (
  application,
  request,
  onSuccess,
  onFailure
) => {
  try {
    const {downPaymentAmount, ...updateRequest} = request;

    if (application.typeCd !== 'Application') {
      await postBindRequest(application.systemId);
    }

    await putApplicationPaymentPlans(
        application.systemId,
        transformPaymentPlanRequest(updateRequest)
    );

    if (downPaymentAmount) {
      await postApplicationDownPayment(application.systemId, {
        downPaymentAmount,
        electronicPaymentSource: transformPaymentPlanRequest(request)
          .electronicPaymentSource,
      });
    }

    await postCloseoutRequest(application.systemId);

    const closeoutApp = await getApplication(application.systemId);

    if (!!closeoutApp.validationError) {
      const errors = closeoutApp.validationError.filter(
        (error) => error.typeCd !== 'Approval'
      );

      onFailure(errors.map((error) => error.msg).join(' '));
    } else {
      await postIssueRequest(application.systemId);
      onSuccess();
    }
  } catch (error) {
    if (!PRODUCTION) {
      console.error(error);
    }
    if ((error.response && (error.response.status === 503)) || (error.message == 'Network Error')) {
      navigateWithData('/server-maintenance', application.applicationNumber);
    } else {
      onFailure(error.message);
    }
  }
};

export const getPaymentPlans = async (applicationId) => {
  return await api.get(`/applications/${applicationId}/paymentPlans`);
};

export const getPaymentSources = async (applicationId) => {
  return await api.get(`/applications/${applicationId}/paymentSources`);
};
export const getAccountTypes = async () => {
  return await api.get(`/coderefs/UWPolicy/policy/pay-plan/account-type`);
};
export const getBankAccountTypes = async () => {
  return await api.get(`/coderefs/UWPolicy/policy/pay-plan/bank-account-type`);
};

export const postBindRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/bindRequest');
export const updateApplicationPaymentPlans = async (applicationId, request) =>
  api.post(
    '/applications/' + applicationId + '/updatePaymentPlanRequest',
    request
  );
export const postApplicationDownPayment = (applicationId, request) =>
  api.post('/applications/' + applicationId + '/downPaymentRequest', request);
export const postCloseoutRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/closeoutRequest');
export const postIssueRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/issueRequest');

const transformPaymentPlanRequest = (paymentPlan) => {
  const { payPlanTypeCd, ...request } = paymentPlan;
  const { id, ...eps } = request.electronicPaymentSource;
  const {
    achName,
    achBankName,
    achBankAccountTypeCd,
    achBankAccountNumber,
    achRoutingNumber,
    achStandardEntryClassCd,
    ...creditCardSource
  } = eps;
  const {
    creditCardAuthorizationCd,
    creditCardAuthorizationMessage,
    creditCardExpirationMonth,
    creditCardExpirationYr,
    creditCardHolderName,
    creditCardNumber,
    creditCardSecurityCd,
    creditCardTypeCd,
    customerPaymentProfileId,
    customerProfileId,
    reminderDt,
    ...achSource
  } = eps;
  switch (request.paymentTypeCd) {
    case 'ACH': {
      return {
        ...request,
        electronicPaymentSource:
          payPlanTypeCd === 'Automatic'
            ? {...EMPTY_ELECTRONIC_PAYMENT_SOURCE, ...achSource}
            : undefined,
      };
    }
    case 'Credit Card': {
      return {
        ...request,
        electronicPaymentSource:
          payPlanTypeCd === 'Automatic'
            ? {
              ...EMPTY_ELECTRONIC_PAYMENT_SOURCE,
              ...creditCardSource,
            }
            : undefined,
      };
    }
    default:
      return request;
  }
};

export const EMPTY_ELECTRONIC_PAYMENT_SOURCE = {
  achBankAccountNumber: '',
  achBankAccountTypeCd: '',
  achBankName: '',
  achName: '',
  achRoutingNumber: '',
  achStandardEntryClassCd: '',
  creditCardNumber: '',
  customerPaymentProfileId: '',
  customerProfileId: '',
  methodCd: '',
};

export async function paymentRequest(model, setState) {
  setState({
    loadingNext: true,
    inactive: true,
    paymentLoading: true,
  });

  const path = 'basicPolicy.electronicPaymentSource';
  const electronicPaymentSource = model.getValue(path);

  let updateRequest = {};

  if (electronicPaymentSource.existCardId === 'NewCard'){
    delete electronicPaymentSource.id;
  }
  delete electronicPaymentSource.existCardId;

  if (electronicPaymentSource.methodCd === 'CC') {

    delete electronicPaymentSource.sourceTypeCd;

    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      /*
        paymentTypeCd is hard code to 'Credit Card' when user choose Credit Card Payment
        because when the data send to server, it has to be 'Credit Card' not 'CC',
        but the value of electronicPaymentSource.methodCd is 'CC'
       */
      paymentTypeCd: 'Credit Card',
    };

  } else if (electronicPaymentSource.methodCd === 'ACH') {
    const firstNamePath = 'insured.partyInfo[0].nameInfo.givenName';
    const firstName = model.getValue(firstNamePath);

    const lastNamePath = 'insured.partyInfo[0].nameInfo.surname';
    const lastName = model.getValue(lastNamePath);

    delete electronicPaymentSource.sourceTypeCd;

    electronicPaymentSource['achName'] = `${firstName} ${lastName}`;

    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      paymentTypeCd: electronicPaymentSource.methodCd,
    };
  } else {
    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      paymentTypeCd: electronicPaymentSource.methodCd,
      paymentId: electronicPaymentSource.paymentId,
    };
  }

  const downPayment = model.getValue('transactionInfo.paymentAmt');

  const postRequest = electronicPaymentSource.methodCd === 'CC' ?
    {
      downPaymentAmount: downPayment,
      electronicPaymentSource: {...electronicPaymentSource, methodCd: 'Credit Card'}
    } :
    {
      downPaymentAmount: downPayment,
      electronicPaymentSource
    }


  try {
    const result = await updateApplicationPaymentPlans(model.data.systemId, updateRequest);
    if (result === undefined) return (null);  // stop if not successful
    sendGoogleAnalyticsEvent({...GA_CONFIGURATION.Payment_Method, event_label: updateRequest.payPlanCd});

    await postApplicationDownPayment(model.data.systemId, postRequest);

    await postCloseoutRequest(model.data.systemId).then(async () => {
      const closeoutApp = await getApplication(model.data.systemId);

      let issues = [];

      traverseObject(closeoutApp, (obj, path) => {
        if (obj.issues) {
          obj.issues.forEach((issue) => {
            if (issue.msg) {
              issues.push(issue);
            }
          });
        }
      });

      if (issues.length > 0) {
        setState({
          loadingNext: false,
          inactive: false,
          paymentLoading: false,
        });
        return navigateWithData('/quote-summary', closeoutApp);
      } else {
        const customerInfo = await postIssueRequest(model.data.systemId)
            .then(async (res) => await api.get(res.headers.location))
            .then(async (res) => {
              return res.data;
            });

        setState({
          loadingNext: false,
          inactive: false,
          paymentLoading: false,
        });

        return navigateWithData('/payment-successful', {
          ...model.data,
          customerRef: customerInfo.customerRef,
          policyNumber: customerInfo.basicPolicy.policyNumber,
          policyRef: customerInfo.systemId,
        });
      }
    });
  } catch (error) {
    setState({
      loadingNext: false,
      inactive: false,
      paymentLoading: false,
      emailQuoteErrors: error.message,
    });
    await handleError(error, model.data.systemId, model.data.applicationNumber, 'paymentRequest');
  }
}

export const buyBindRequest = async (
  systemId,
  applicationNumber,
  navigate,
  navigateToStep,
  lastStep,
  setState,
  pullApplication
) => {
  try {
    await postBindRequest(systemId);
    //TODO: endpoint need to be changed later
    const obtainDataReport = (systemId) => {
      return api
        .post(`/applications/${systemId}/dataReportCheckAndApplyRequest`)
        .then((res) => {
          if (res.data.pendingDataReportExists === 'false' || res.data.numberOfFailedDataReports > 0) return res.data;
          return obtainDataReport(systemId);
        });
    };
    const res = await obtainDataReport(systemId);
    if (res.numberOfFailedDataReports === 0) {
      const app = await getApplication(systemId);
      await putApplication(app);
      navigateToStep(lastStep);
      pullApplication();
    } else {
      await onSubmitAgent(systemId,applicationNumber);
    }
  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'buyBindRequest');
  } finally{
    setState({
      loadingBack: false,
      loadingNext: false,
    });
    scrollTo(0, 0);
  }
};

export const driverDataReportRequest = async (
    systemId,
    applicationNumber,
    navigate,
    navigateToStep,
    lastStep,
    emptyStep,
    setState,
    pullApplication,
    disabledSteps,
    setDisabledSteps
) => {
  const POLL_INTERVAL = 1000;
  const POLL_ATTEMPTS = 60;
  try {
    const index = disabledSteps.indexOf('vehiclesAndDrivers')
    if (index !== -1){
      const newDisabledSteps = [...disabledSteps];
      newDisabledSteps.splice(index,1);
      setDisabledSteps(newDisabledSteps);
    }
    const requestDataReportStatus = async() => {
      return api
        .get(`/applications/${systemId}/dataReportRequests/${dataReportId}/status`);
    }

    const validateResponse = res => res.status !== 202;

    let dataReportId = "";
    let completed = false;
    const prefillRequestResponse = await api.post(
      `/applications/${systemId}/dataReportPrefillRequest`
    );
    if (prefillRequestResponse.status === 200) {
      dataReportId = prefillRequestResponse.data.dataReportRequestId;

      const statusCheckResult = await pollRequest({
        fn: requestDataReportStatus,
        validate: validateResponse,
        interval: POLL_INTERVAL,
        maxAttempts: POLL_ATTEMPTS,
      });

      if (statusCheckResult.status && statusCheckResult.status === 200) {
        completed = statusCheckResult.data.requestStatusCd === "Applied";

        await pullApplication();
      } else {
        console.log("Data Report Status Failure Status: " + statusCheckResult.response.status + " Error Message: " + statusCheckResult.message);
      }

      if (completed) {
        navigateToStep(lastStep);
      } else {
        navigateToStep(emptyStep);
        setDisabledSteps([...disabledSteps,'vehiclesAndDrivers']);
      }
      setState({
        loadingBack: false,
        loadingNext: false,
      });
      scrollTo(0, 0);
    }

  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'buyBindRequest');
  }
};

const pollRequest = async ({ fn, validate, interval, maxAttempts }) => {
  let attempts = 0;

  const executePoll = async (resolve, reject) => {
    try {
      const result = await fn();
      attempts++;

      if (validate(result)) {
        return resolve(result);
      } else if (maxAttempts && attempts === maxAttempts) {
        return reject(new Error('Exceeded max attempts'));
      } else {
        setTimeout(executePoll, interval, resolve, reject);
      }
    } catch (error) {
      return resolve(error);
    }
  };

  return new Promise(executePoll);
};